import { useState, useRef, useEffect } from 'react';
import { Hole, CalibrationConfig, Rectangle } from '../types';
import { CameraView } from './CameraView';
import { EdgeGuidedDrawMode } from './EdgeGuidedDrawMode';
import { calculateZonesFromReferencePoints, validateReferencePoints, ReferencePoints } from '../utils/geometricCalibration';

const ZONE_VALUES = ['START', 'GUTTER', 10, 20, 30, 40, 50, 100] as const;
const STORAGE_KEY = 'skeescore-calibration';

type ZoneValue = typeof ZONE_VALUES[number];
type HoleShape = 'circle' | 'rectangle';
type CalibrationMode = 'manual' | 'smart-manual' | 'edge-draw';

export function CalibrationUI({ onComplete }: { onComplete: (config: CalibrationConfig) => void }) {
  // Mode selection
  const [calibrationMode, setCalibrationMode] = useState<CalibrationMode>('manual');

  // Existing state (for manual/auto modes)
  const [holes, setHoles] = useState<Hole[]>([]);
  const [startZone, setStartZone] = useState<Rectangle | undefined>();
  const [gutterZone, setGutterZone] = useState<Rectangle | undefined>();
  const [selectedValue, setSelectedValue] = useState<ZoneValue>(10);
  const [defaultRadius, setDefaultRadius] = useState<number>(40);
  const [holeShape, setHoleShape] = useState<HoleShape>('circle');
  const [isCalibrating, setIsCalibrating] = useState(true);
  const [isDrawingRect, setIsDrawingRect] = useState(false);
  const [rectStart, setRectStart] = useState<{ x: number; y: number } | null>(null);
  const [saveMessage, setSaveMessage] = useState<string>('');
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const overlayContainerRef = useRef<HTMLDivElement | null>(null);

  // Smart Manual mode state
  const [referencePoints, setReferencePoints] = useState<Partial<ReferencePoints>>({});
  const [currentReferenceStep, setCurrentReferenceStep] = useState<'center30pt' | 'left100pt' | 'right100pt' | 'complete'>('center30pt');

  // Handle applying calibration from annotation
  const handleApplyCalibration = (config: CalibrationConfig) => {
    setHoles(config.holes);
    setStartZone(config.startZone);
    setGutterZone(config.gutterZone);

    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));

    console.log('✅ Calibration applied from annotation:', config);
  };

  // Load saved calibration on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const config: CalibrationConfig = JSON.parse(saved);
        setHoles(config.holes);
        setStartZone(config.startZone);
        setGutterZone(config.gutterZone);
      } catch (e) {
        console.error('Failed to load calibration:', e);
      }
    }
  }, []);

  const handleCanvasReady = (canvas: HTMLCanvasElement, video: HTMLVideoElement) => {
    canvasRef.current = canvas;
    videoRef.current = video;
  };

  const getCanvasCoords = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!canvasRef.current) return null;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = canvasRef.current.width / rect.width;
    const scaleY = canvasRef.current.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isCalibrating) return;
    const coords = getCanvasCoords(e);
    if (!coords) return;

    // For START and GUTTER, always use rectangle (drag)
    if (selectedValue === 'START' || selectedValue === 'GUTTER') {
      setIsDrawingRect(true);
      setRectStart(coords);
      return;
    }

    // For scoring holes
    if (holeShape === 'circle') {
      // Click to add circular hole
      const newHole: Hole = {
        id: `hole-${Date.now()}`,
        x: coords.x,
        y: coords.y,
        radius: defaultRadius,
        value: selectedValue as number,
        shape: 'circle',
      };
      setHoles(prev => [...prev, newHole]);
    } else {
      // Start drawing rectangle hole
      setIsDrawingRect(true);
      setRectStart(coords);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawingRect || !rectStart) return;
    // Rectangle preview could be added here
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawingRect || !rectStart) return;
    const coords = getCanvasCoords(e);
    if (!coords) return;

    // Calculate rectangle
    const x = Math.min(rectStart.x, coords.x);
    const y = Math.min(rectStart.y, coords.y);
    const width = Math.abs(coords.x - rectStart.x);
    const height = Math.abs(coords.y - rectStart.y);

    if (selectedValue === 'START') {
      setStartZone({ x, y, width, height });
    } else if (selectedValue === 'GUTTER') {
      setGutterZone({ x, y, width, height });
    } else {
      // Add rectangle hole
      const newHole: Hole = {
        id: `hole-${Date.now()}`,
        x,
        y,
        radius: 0, // Not used for rectangles
        value: selectedValue as number,
        shape: 'rectangle',
        width,
        height,
      };
      setHoles(prev => [...prev, newHole]);
    }

    setIsDrawingRect(false);
    setRectStart(null);
  };

  // Smart Manual mode click handler
  const handleSmartManualClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isCalibrating || currentReferenceStep === 'complete') return;
    const coords = getCanvasCoords(e);
    if (!coords) return;

    const newPoint = { x: coords.x, y: coords.y };

    if (currentReferenceStep === 'center30pt') {
      setReferencePoints({ ...referencePoints, center30pt: newPoint });
      setCurrentReferenceStep('left100pt');
    } else if (currentReferenceStep === 'left100pt') {
      setReferencePoints({ ...referencePoints, left100pt: newPoint });
      setCurrentReferenceStep('right100pt');
    } else if (currentReferenceStep === 'right100pt') {
      setReferencePoints({ ...referencePoints, right100pt: newPoint });
      setCurrentReferenceStep('complete');
    }
  };

  const removeHole = (id: string) => {
    setHoles(prev => prev.filter(h => h.id !== id));
  };

  const clearAll = () => {
    setHoles([]);
    setStartZone(undefined);
    setGutterZone(undefined);
  };

  // Get hole color matching CameraView overlay colors
  const getHoleColor = (value: number): string => {
    switch (value) {
      case 10: return '#FF6B6B'; // Red
      case 20: return '#FFA500'; // Orange
      case 30: return '#FFD700'; // Gold
      case 40: return '#4ECDC4'; // Teal
      case 50: return '#45B7D1'; // Blue
      case 100: return '#96CEB4'; // Green
      default: return '#888888'; // Gray
    }
  };

  // Export current calibration config as JSON file
  const exportConfig = () => {
    const config: CalibrationConfig = {
      holes,
      startZone,
      gutterZone,
      timestamp: Date.now(),
    };

    const dataStr = JSON.stringify(config, null, 2);

    // Log to console so it can be copied to calibration-configs folder
    console.log('\n📥 ===== CALIBRATION CONFIG EXPORT =====');
    console.log('Copy this to: calibration-configs/config-[description].json');
    console.log(dataStr);
    console.log('=====================================\n');

    // Also download as backup
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `calibration-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSaveMessage('📥 Config exported to console & download!');
    setTimeout(() => setSaveMessage(''), 3000);
  };

  const saveCalibration = () => {
    const config: CalibrationConfig = {
      holes,
      startZone,
      gutterZone,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));

    // Log calibration data for training/debugging
    logCalibrationData(config);

    setSaveMessage('✅ Calibration saved!');
    setTimeout(() => setSaveMessage(''), 3000); // Clear message after 3 seconds
  };

  const logCalibrationData = (config: CalibrationConfig) => {
    console.log('\n📊 ===== CALIBRATION DATA FOR TRAINING =====');
    console.log('Timestamp:', new Date(config.timestamp).toISOString());

    // Log START zone
    if (config.startZone) {
      const s = config.startZone;
      console.log(`🟢 START ▭ (${Math.round(s.width)}x${Math.round(s.height)}) at (${Math.round(s.x)}, ${Math.round(s.y)})`);
    }

    // Log GUTTER zone
    if (config.gutterZone) {
      const g = config.gutterZone;
      console.log(`🔴 GUTTER ▭ (${Math.round(g.width)}x${Math.round(g.height)}) at (${Math.round(g.x)}, ${Math.round(g.y)})`);
    }

    // Log holes sorted by value (descending)
    const sortedHoles = [...config.holes].sort((a, b) => b.value - a.value);
    sortedHoles.forEach(hole => {
      if (hole.shape === 'rectangle') {
        console.log(`${hole.value}pt ▭ (${Math.round(hole.width || 0)}x${Math.round(hole.height || 0)}) at (${Math.round(hole.x)}, ${Math.round(hole.y)})`);
      } else {
        console.log(`${hole.value}pt ⭕ (r=${Math.round(hole.radius || 0)}) at (${Math.round(hole.x)}, ${Math.round(hole.y)})`);
      }
    });

    console.log('============================================\n');
  };





  const completeCalibration = () => {
    if (holes.length === 0) {
      alert('Please add at least one hole before completing calibration.');
      return;
    }

    const config: CalibrationConfig = {
      holes,
      startZone,
      gutterZone,
      timestamp: Date.now(),
    };
    // Save calibration to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    // Show save message briefly
    setSaveMessage('✅ Calibration saved!');
    setTimeout(() => setSaveMessage(''), 2000);
    // Complete calibration and switch to scoring
    setIsCalibrating(false);
    onComplete(config);
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '10px' }}>SkeeScore - Calibration</h1>

        {/* Mode Selector Tabs */}
        {isCalibrating && (
          <div style={{
            marginBottom: '20px',
            display: 'flex',
            gap: '10px',
            borderBottom: '2px solid #ddd',
            paddingBottom: '10px'
          }}>
            <button
              onClick={() => setCalibrationMode('manual')}
              style={{
                padding: '10px 20px',
                backgroundColor: calibrationMode === 'manual' ? '#4CAF50' : '#f5f5f5',
                color: calibrationMode === 'manual' ? 'white' : 'black',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                transition: 'all 0.2s',
              }}
            >
              🎯 Manual
            </button>

            <button
              onClick={() => {
                setCalibrationMode('smart-manual');
                setReferencePoints({});
                setCurrentReferenceStep('center30pt');
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: calibrationMode === 'smart-manual' ? '#2196F3' : '#f5f5f5',
                color: calibrationMode === 'smart-manual' ? 'white' : 'black',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                transition: 'all 0.2s',
              }}
            >
              🎯 Smart Manual
            </button>

            <button
              onClick={() => setCalibrationMode('edge-draw')}
              style={{
                padding: '10px 20px',
                backgroundColor: calibrationMode === 'edge-draw' ? '#FF5722' : '#f5f5f5',
                color: calibrationMode === 'edge-draw' ? 'white' : 'black',
                border: 'none',
                borderRadius: '8px 8px 0 0',
                cursor: 'pointer',
                fontWeight: 'bold',
                fontSize: '14px',
                transition: 'all 0.2s',
              }}
            >
              🎨 Edge + Draw
            </button>
          </div>
        )}

        {saveMessage && (
          <div style={{
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#4CAF50',
            color: 'white',
            borderRadius: '8px',
            fontWeight: 'bold',
            textAlign: 'center',
          }}>
            {saveMessage}
          </div>
        )}



        {isCalibrating && calibrationMode === 'manual' && (
          <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Left Column - Controls */}
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', minHeight: '350px' }}>
              <div>
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ marginRight: '10px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Create Zones</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                    {ZONE_VALUES.map(value => (
                      <button
                        key={value}
                        onClick={() => setSelectedValue(value)}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: selectedValue === value ? '#4CAF50' : '#ddd',
                          color: selectedValue === value ? 'white' : 'black',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                        }}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>

                {typeof selectedValue === 'number' && (
                  <>
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ marginRight: '10px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Shape Type</label>
                      <button
                        onClick={() => setHoleShape('circle')}
                        style={{
                          ...buttonStyle(holeShape === 'circle' ? '#4CAF50' : '#ddd'),
                          color: holeShape === 'circle' ? 'white' : 'black',
                        }}
                      >
                        ⭕ Circle (Click)
                      </button>
                      <button
                        onClick={() => setHoleShape('rectangle')}
                        style={{
                          ...buttonStyle(holeShape === 'rectangle' ? '#4CAF50' : '#ddd'),
                          color: holeShape === 'rectangle' ? 'white' : 'black',
                        }}
                      >
                        ▭ Rectangle (Drag)
                      </button>
                    </div>

                    {holeShape === 'circle' && (
                      <div style={{ marginBottom: '15px' }}>
                        <label style={{ marginRight: '10px', fontWeight: 'bold', display: 'block', marginBottom: '8px' }}>Hole Radius</label>
                        <input
                          type="range"
                          min="20"
                          max="400"
                          value={defaultRadius}
                          onChange={e => setDefaultRadius(Number(e.target.value))}
                          style={{ width: '100%', maxWidth: '300px' }}
                        />
                        <span style={{ marginLeft: '10px' }}>{defaultRadius}px</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button onClick={saveCalibration} style={{ ...buttonStyle('#2196F3'), width: '100%' }}>
                  Save Calibration
                </button>
                <button onClick={exportConfig} style={{ ...buttonStyle('#9C27B0'), width: '100%' }}>
                  📥 Export Config
                </button>
                <button onClick={completeCalibration} style={{ ...buttonStyle('#4CAF50'), width: '100%' }}>
                  Start Scoring
                </button>
              </div>
            </div>

            {/* Right Column - Zones & Holes List */}
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <strong style={{ display: 'block', marginBottom: '10px' }}>Mapped Zones</strong>
              <ul style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '5px', paddingLeft: '20px' }}>
                {startZone && (
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4CAF50', marginRight: '8px', borderRadius: '2px' }}></span>
                    START ▭ ({Math.round(startZone.width)}x{Math.round(startZone.height)}) at ({Math.round(startZone.x)}, {Math.round(startZone.y)})
                    <button
                      onClick={() => setStartZone(undefined)}
                      style={{ marginLeft: '10px', padding: '2px 8px', fontSize: '12px' }}
                    >
                      Remove
                    </button>
                  </li>
                )}
                {gutterZone && (
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f44336', marginRight: '8px', borderRadius: '2px' }}></span>
                    GUTTER ▭ ({Math.round(gutterZone.width)}x{Math.round(gutterZone.height)}) at ({Math.round(gutterZone.x)}, {Math.round(gutterZone.y)})
                    <button
                      onClick={() => setGutterZone(undefined)}
                      style={{ marginLeft: '10px', padding: '2px 8px', fontSize: '12px' }}
                    >
                      Remove
                    </button>
                  </li>
                )}
                {holes.map(hole => {
                  const shapeInfo = hole.shape === 'rectangle'
                    ? `${Math.round(hole.width || 0)}x${Math.round(hole.height || 0)}`
                    : `r=${Math.round(hole.radius || 0)}`;
                  const holeColor = getHoleColor(hole.value);
                  return (
                    <li key={hole.id} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: holeColor, marginRight: '8px', borderRadius: '2px' }}></span>
                      {hole.value}pt {hole.shape === 'rectangle' ? '▭' : '⭕'} ({shapeInfo}) at ({Math.round(hole.x)}, {Math.round(hole.y)})
                      <button
                        onClick={() => removeHole(hole.id)}
                        style={{ marginLeft: '10px', padding: '2px 8px', fontSize: '12px' }}
                      >
                        Remove
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button onClick={clearAll} style={{ ...buttonStyle('#f44336'), width: '100%', marginTop: '15px' }}>
                Clear All Zones
              </button>
            </div>
          </div>
      )}

        {/* Smart Manual Mode - 3-point reference calibration */}
        {isCalibrating && calibrationMode === 'smart-manual' && (
          <div style={{ marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            {/* Left Column - Instructions */}
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <h3 style={{ marginTop: 0, marginBottom: '15px', color: '#2196F3' }}>📍 Click 3 Reference Points</h3>

              <div style={{ marginBottom: '20px' }}>
                <div style={{
                  padding: '12px',
                  backgroundColor: currentReferenceStep === 'center30pt' ? '#E3F2FD' : '#f5f5f5',
                  borderLeft: currentReferenceStep === 'center30pt' ? '4px solid #2196F3' : '4px solid #ddd',
                  marginBottom: '10px',
                  borderRadius: '4px'
                }}>
                  <strong>1. {referencePoints.center30pt ? '✅' : '⭕'} Center 30pt Hole</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
                    Click the center of the 30-point hole (center ring)
                  </p>
                  {referencePoints.center30pt && (
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#2196F3' }}>
                      ✓ ({Math.round(referencePoints.center30pt.x)}, {Math.round(referencePoints.center30pt.y)})
                    </p>
                  )}
                </div>

                <div style={{
                  padding: '12px',
                  backgroundColor: currentReferenceStep === 'left100pt' ? '#E3F2FD' : '#f5f5f5',
                  borderLeft: currentReferenceStep === 'left100pt' ? '4px solid #2196F3' : '4px solid #ddd',
                  marginBottom: '10px',
                  borderRadius: '4px'
                }}>
                  <strong>2. {referencePoints.left100pt ? '✅' : '⭕'} Left 100pt Hole</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
                    Click the center of the LEFT 100-point hole (top left)
                  </p>
                  {referencePoints.left100pt && (
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#2196F3' }}>
                      ✓ ({Math.round(referencePoints.left100pt.x)}, {Math.round(referencePoints.left100pt.y)})
                    </p>
                  )}
                </div>

                <div style={{
                  padding: '12px',
                  backgroundColor: currentReferenceStep === 'right100pt' ? '#E3F2FD' : '#f5f5f5',
                  borderLeft: currentReferenceStep === 'right100pt' ? '4px solid #2196F3' : '4px solid #ddd',
                  marginBottom: '10px',
                  borderRadius: '4px'
                }}>
                  <strong>3. {referencePoints.right100pt ? '✅' : '⭕'} Right 100pt Hole</strong>
                  <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666' }}>
                    Click the center of the RIGHT 100-point hole (top right)
                  </p>
                  {referencePoints.right100pt && (
                    <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#2196F3' }}>
                      ✓ ({Math.round(referencePoints.right100pt.x)}, {Math.round(referencePoints.right100pt.y)})
                    </p>
                  )}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                <button
                  onClick={() => {
                    if (validateReferencePoints(referencePoints)) {
                      const config = calculateZonesFromReferencePoints(referencePoints as ReferencePoints);
                      setHoles(config.holes);
                      setStartZone(config.startZone);
                      setGutterZone(config.gutterZone);
                      setCurrentReferenceStep('complete');
                      setSaveMessage('✅ Zones calculated! Review and adjust if needed.');
                      setTimeout(() => setSaveMessage(''), 3000);
                    } else {
                      setSaveMessage('❌ Invalid reference points. Please check your selections.');
                      setTimeout(() => setSaveMessage(''), 3000);
                    }
                  }}
                  disabled={!validateReferencePoints(referencePoints)}
                  style={{
                    ...buttonStyle(validateReferencePoints(referencePoints) ? '#2196F3' : '#ddd'),
                    color: 'white',
                    opacity: validateReferencePoints(referencePoints) ? 1 : 0.5,
                    cursor: validateReferencePoints(referencePoints) ? 'pointer' : 'not-allowed',
                  }}
                >
                  🧮 Calculate All Zones
                </button>

                <button
                  onClick={saveCalibration}
                  style={{ ...buttonStyle('#4CAF50'), color: 'white' }}
                >
                  💾 Save Calibration
                </button>

                <button
                  onClick={exportConfig}
                  style={{ ...buttonStyle('#9C27B0'), color: 'white' }}
                >
                  📥 Export Config
                </button>
              </div>
            </div>

            {/* Right Column - Calculated Zones */}
            <div style={{ backgroundColor: 'white', padding: '15px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
              <strong style={{ display: 'block', marginBottom: '10px' }}>Calculated Zones</strong>
              <ul style={{ maxHeight: '400px', overflowY: 'auto', marginTop: '5px', paddingLeft: '20px' }}>
                {startZone && (
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#4CAF50', marginRight: '8px', borderRadius: '2px' }}></span>
                    START ▭ ({Math.round(startZone.width)}x{Math.round(startZone.height)}) at ({Math.round(startZone.x)}, {Math.round(startZone.y)})
                  </li>
                )}
                {gutterZone && (
                  <li style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                    <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: '#f44336', marginRight: '8px', borderRadius: '2px' }}></span>
                    GUTTER ▭ ({Math.round(gutterZone.width)}x{Math.round(gutterZone.height)}) at ({Math.round(gutterZone.x)}, {Math.round(gutterZone.y)})
                  </li>
                )}
                {holes.map(hole => {
                  const shapeInfo = hole.shape === 'rectangle'
                    ? `${Math.round(hole.width || 0)}x${Math.round(hole.height || 0)}`
                    : `r=${Math.round(hole.radius || 0)}`;
                  const holeColor = getHoleColor(hole.value);
                  return (
                    <li key={hole.id} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center' }}>
                      <span style={{ display: 'inline-block', width: '12px', height: '12px', backgroundColor: holeColor, marginRight: '8px', borderRadius: '2px' }}></span>
                      {hole.value}pt {hole.shape === 'rectangle' ? '▭' : '⭕'} ({shapeInfo}) at ({Math.round(hole.x)}, {Math.round(hole.y)})
                    </li>
                  );
                })}
              </ul>
              <button onClick={clearAll} style={{ ...buttonStyle('#f44336'), width: '100%', marginTop: '15px' }}>
                Clear All Zones
              </button>
            </div>
          </div>
        )}

        {/* Edge-Guided Draw Mode - Combines edge detection with drawing */}
        {isCalibrating && calibrationMode === 'edge-draw' && (
          <EdgeGuidedDrawMode
            videoRef={videoRef}
            canvasRef={canvasRef}
            overlayContainerRef={overlayContainerRef}
            onApplyCalibration={handleApplyCalibration}
          />
        )}

        {/* Camera View - Only show for manual mode or when not in annotation modes */}
        {(calibrationMode === 'manual' || !isCalibrating) && (
          <div
            onMouseDown={calibrationMode === 'manual' ? handleMouseDown : undefined}
            onMouseMove={calibrationMode === 'manual' ? handleMouseMove : undefined}
            onMouseUp={calibrationMode === 'manual' ? handleMouseUp : undefined}
            style={{ cursor: isCalibrating && calibrationMode === 'manual' ? 'crosshair' : 'default' }}
          >
            <CameraView
              holes={holes}
              startZone={startZone}
              gutterZone={gutterZone}
              onCanvasReady={handleCanvasReady}
            />
          </div>
        )}

        {/* Camera View for Smart Manual mode with reference point overlay */}
        {isCalibrating && calibrationMode === 'smart-manual' && (
          <div style={{ position: 'relative', marginTop: '20px' }}>
            <div
              onClick={handleSmartManualClick}
              style={{ cursor: currentReferenceStep !== 'complete' ? 'crosshair' : 'default' }}
            >
              <CameraView
                holes={holes}
                startZone={startZone}
                gutterZone={gutterZone}
                onCanvasReady={handleCanvasReady}
              />
            </div>
            {/* Overlay for reference point markers */}
            <svg
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
              }}
            >
              {referencePoints.center30pt && (
                <g>
                  <circle
                    cx={referencePoints.center30pt.x}
                    cy={referencePoints.center30pt.y}
                    r="10"
                    fill="#2196F3"
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text
                    x={referencePoints.center30pt.x}
                    y={referencePoints.center30pt.y}
                    fill="white"
                    fontSize="14"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    1
                  </text>
                </g>
              )}
              {referencePoints.left100pt && (
                <g>
                  <circle
                    cx={referencePoints.left100pt.x}
                    cy={referencePoints.left100pt.y}
                    r="10"
                    fill="#2196F3"
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text
                    x={referencePoints.left100pt.x}
                    y={referencePoints.left100pt.y}
                    fill="white"
                    fontSize="14"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    2
                  </text>
                </g>
              )}
              {referencePoints.right100pt && (
                <g>
                  <circle
                    cx={referencePoints.right100pt.x}
                    cy={referencePoints.right100pt.y}
                    r="10"
                    fill="#2196F3"
                    stroke="white"
                    strokeWidth="2"
                  />
                  <text
                    x={referencePoints.right100pt.x}
                    y={referencePoints.right100pt.y}
                    fill="white"
                    fontSize="14"
                    fontWeight="bold"
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    3
                  </text>
                </g>
              )}
            </svg>
          </div>
        )}

        {/* Camera View with Overlay for Edge-Draw mode */}
        {isCalibrating && calibrationMode === 'edge-draw' && (
          <div style={{ position: 'relative', marginTop: '20px' }}>
            <CameraView
              holes={[]}
              startZone={undefined}
              gutterZone={undefined}
              onCanvasReady={handleCanvasReady}
            />
            {/* Overlay container for annotation canvases */}
            <div
              ref={overlayContainerRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none' // Let children control their own pointer events
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function buttonStyle(bgColor: string): React.CSSProperties {
  return {
    margin: '0 5px',
    padding: '10px 20px',
    backgroundColor: bgColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 'bold',
  };
}

